import { QUESTIONS } from "./questions";
import type { QuizQuestion } from "./types";

/**
 * Legacy quiz-shape guard (ClickUp 86bb0fwu5).
 *
 * The quiz has been rewritten twice:
 *
 *   v1 (pre-2026-07-20):  QUESTIONS.length = 17  (13 personality + 4 profile)
 *   v2 (2026-07-20):      QUESTIONS.length = 24  (20 personality + 4 profile)
 *   v3 (2026-09-21):      QUESTIONS.length = 19  (15 personality + 4 profile)
 *
 * Quiz results saved under an older version still hold answers whose option text came
 * from that era's question set. Nothing migrates them — `versionManager` only remaps
 * CATEGORY NAMES (see categoryMigration.ts), never answer-array length.
 *
 * The failure mode this guards is silent, not a crash: the existing call sites resolve
 * a stored answer by matching its text against the CURRENT question's options via
 * `findIndex`, and fall back to `0` when there is no match. For a legacy record every
 * match fails, so every answer collapses to option 0 and renders as a confident,
 * completely wrong response labelled against a question the client never saw.
 *
 * So the guarantee here is on the RESULT, not on any one call site's construction:
 * resolve returns `null` for an answer that did not genuinely match, and reports
 * `isLegacy` so callers can suppress the per-question breakdown rather than print
 * fabricated answers.
 */

/** Question counts from prior quiz versions. */
export const LEGACY_QUESTION_COUNTS = [17, 24] as const;

/** Answers as stored on a quiz record: `{ answer: <option text> }`. */
export interface StoredAnswer {
  answer: string;
}

export interface ResolvedAnswers {
  /**
   * Per stored answer, the index of the matching option in the CURRENT question set,
   * or `null` when the stored text matches nothing (i.e. it is not trustworthy).
   * Positionally aligned with `QUESTIONS`.
   */
  indices: Array<number | null>;
  /** True when the record predates the 24-question rewrite and cannot be mapped. */
  isLegacy: boolean;
  /** How many stored answers resolved to a real option. */
  matchedCount: number;
  /** How many stored answers were present on the record. */
  answerCount: number;
}

/**
 * Resolve stored answer text against a question set, without inventing fallbacks.
 *
 * A record is treated as legacy when it carries fewer answers than the current
 * question set AND nothing resolved - the two together are what distinguish a
 * genuinely old record from a merely incomplete current one.
 */
export function resolveStoredAnswers(
  stored: StoredAnswer[] | undefined | null,
  questions: QuizQuestion[] = QUESTIONS
): ResolvedAnswers {
  const answers = Array.isArray(stored) ? stored : [];

  const indices = questions.map((question, idx) => {
    const item = answers[idx];
    if (!item || typeof item.answer !== "string") return null;
    const optionIdx = question.options.findIndex(
      (opt) => opt.text === item.answer
    );
    return optionIdx === -1 ? null : optionIdx;
  });

  const matchedCount = indices.filter((i) => i !== null).length;

  // A record from any prior era: either shorter OR longer than current, with no matches.
  // v1 records have 17 answers, v2 have 24, v3 (current) has 19 — so a v2 record is
  // LONGER than current but still legacy. The key signal is zero matches.
  const isLegacy = answers.length > 0 && answers.length !== questions.length && matchedCount === 0;

  return { indices, isLegacy, matchedCount, answerCount: answers.length };
}

/**
 * Adapt resolved indices to the `number[]` shape the PDF renderer takes.
 *
 * `-1` marks "no trustworthy answer" - the renderer skips those rows. It is used
 * rather than `0` precisely because `0` is a VALID option index and is exactly the
 * value the old fallback used to fabricate.
 */
export function toRenderableIndices(resolved: ResolvedAnswers): number[] {
  return resolved.indices.map((i) => (i === null ? -1 : i));
}
